import React, { useContext } from 'react';
import classnames from 'classnames';
import { Helmet } from 'react-helmet-async';
import { Url } from '@wix/da-url';
import { RuntimeEnvironment } from '../../types';
import { getGlobalClassnamesForContext } from '@wix/da-react-context/pkg/helpers';
import {
  ThemeOptions,
  ThemeSkin,
} from '@wix/da-react-context/pkg/ThemeContext';
import { FlagsObject } from '@wix/da-react-context/pkg/flags/types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useMetaRobots } from '@wix/da-react-app/pkg/client/Head/hooks/useMetaRobots';

export interface Props {
  pageTitle?: string;
  pageDescription?: string;
  pageRating?: string;
  pageThumb?: string;
  pageUrl: string;
  isMobile: boolean;
  isIOS?: boolean;
  environment?: RuntimeEnvironment;
  language: string;
  prevUrl?: string;
  nextUrl?: string;
  theme?: ThemeOptions;
  themeSkin?: ThemeSkin;
  bodyClassName?: string;
  flags?: FlagsObject;
  hasNewChatMessage?: boolean;
}

const Head: React.FC<Props> = ({
  pageTitle = 'DeviantArt',
  pageDescription = "DeviantArt is the world's largest online social community for artists and art enthusiasts, allowing people to connect through the creation and sharing of art.",
  pageRating,
  pageUrl,
  pageThumb = Url.staticCDNLink('minish/main/logo/card_black_large.png'),
  isMobile,
  isIOS,
  environment,
  language,
  prevUrl,
  nextUrl,
  theme,
  flags,
  themeSkin,
  bodyClassName,
  hasNewChatMessage,
}) => {
  const { isLoggedIn, device } = useContext(SessionContext);
  const robots = useMetaRobots();
  return (
    <Helmet
      htmlAttributes={{
        lang: language,
      }}
      bodyAttributes={{
        class: classnames(
          getGlobalClassnamesForContext({
            isMobile,
            theme,
            flags,
            themeSkin,
            isLoggedIn,
          }),
          bodyClassName
        ),
      }}
    >
      <meta charSet="utf-8" />
      <title>{pageTitle}</title>
      {preconnectDomains()}
      {preloadFonts()}
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href={Url.staticCDNLink('eclipse/icons/pwa/ios-180.png')}
      />
      <link
        rel="apple-touch-icon"
        sizes="152x152"
        href={Url.staticCDNLink('eclipse/icons/pwa/ios-152.png')}
      />
      <link
        rel="apple-touch-icon"
        sizes="120x120"
        href={Url.staticCDNLink('eclipse/icons/pwa/ios-120.png')}
      />
      <link
        rel="apple-touch-icon"
        sizes="76x76"
        href={Url.staticCDNLink('eclipse/icons/pwa/ios-76.png')}
      />
      <link
        rel="icon"
        sizes="192x192"
        href={Url.staticCDNLink('eclipse/icons/android-192.png')}
      />
      <link
        rel="icon"
        sizes="144x144"
        href={Url.staticCDNLink('eclipse/icons/android-144.png')}
      />
      <link
        rel="icon"
        sizes="96x96"
        href={Url.staticCDNLink('eclipse/icons/android-96.png')}
      />
      <link
        rel="icon"
        sizes="48x48"
        href={Url.staticCDNLink('eclipse/icons/android-48.png')}
      />
      <link
        rel="mask-icon"
        href={Url.staticCDNLink('eclipse/icons/touchbar-mark-v2.svg')}
        color="#00e59b"
      />
      <link
        rel="icon"
        sizes="16x16"
        type="image/png"
        href={Url.staticCDNLink('eclipse/icons/favicon-v2-16x16.png')}
      />
      <link
        rel="icon"
        sizes="32x32"
        type="image/png"
        href={Url.staticCDNLink('eclipse/icons/favicon-v2-32x32.png')}
      />
      <link
        href={getFavIcon(environment, hasNewChatMessage)}
        rel="shortcut icon"
      />
      <link rel="canonical" href={Url.buildFromPath(pageUrl)} />
      {prevUrl && <link href={prevUrl} rel="prev" />}
      {nextUrl && <link href={nextUrl} rel="next" />}
      <meta name="application-name" content="DeviantArt" />
      <meta name="description" content={pageDescription} />
      {robots && <meta name="robots" content={robots} />}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={Url.buildFromPath(pageUrl)} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:image" content={pageThumb} />
      <meta property="og:description" content={pageDescription} />
      <meta property="fb:page_id" content="6452638289" />
      <meta property="twitter:account_id" content="1239671" />
      <meta name="twitter:site" content="@deviantart" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageThumb} />
      <meta name="pinterest" content="nopin" />
      <meta name="color-scheme" content="light dark" />
      {/** D1 on dark, L2 on light */}
      <meta
        name="theme-color"
        content={theme === ThemeOptions.DARK ? '#06070d' : '#ffffff'}
      />
      {pageRating && <meta name="rating" content={pageRating} />}
      {device?.isMobileDevice && (
        <meta name="viewport" content={`width=device-width, initial-scale=1`} />
      )}
    </Helmet>
  );
};
Head.displayName = 'Head';

// We preload just the woff2, they are better compressed and have good support
// especially on browsers that support preload :)
function preloadFonts() {
  const fonts = [
    [
      Url.staticCDNLink('misc/symbols/deviantart-symbols-v8-1-4.woff2'),
      'font/woff2',
    ],
    [
      Url.staticCDNLink('fonts/devioussans-1-110/devioussans_w_bd.woff2'),
      'font/woff2',
    ],
    [
      Url.staticCDNLink('fonts/devioussans-1-110/devioussans_w_xbd.woff2'),
      'font/woff2',
    ],
    [
      Url.staticCDNLink('fonts/devioussans-1-110/devioussans_w_lt.woff2'),
      'font/woff2',
    ],
    [
      Url.staticCDNLink('fonts/devioussans-1-110/devioussans_w_md.woff2'),
      'font/woff2',
    ],
    [
      Url.staticCDNLink('fonts/devioussans-1-110/devioussans_w_rg.woff2'),
      'font/woff2',
    ],
  ];

  return fonts.map(font => (
    <link
      key={font[0]}
      rel="preload"
      href={font[0]}
      as="font"
      type={font[1]}
      crossOrigin={'anonymous'}
    />
  ));
}

function preconnectDomains() {
  const domains = [
    'https://a.deviantart.net',
    'https://images-wixmp-ed30a86b8c4ca887773594c2.wixmp.com',
    'https://img-deviantart.wixmp.com',
    'https://st.deviantart.net',
    'https://hub.deviantart.net',
    'https://static.parastorage.com',
    'https://www.wix.com',
    'https://frog.wix.com',
  ];

  return domains.map(domain => (
    <link key={domain} rel="preconnect" href={domain} />
  ));
}

function getFavIcon(env?: RuntimeEnvironment, hasNewChatMessage?: boolean) {
  if (hasNewChatMessage) {
    switch (env) {
      // same icon for dev / rc
      case 'dev':
      case 'rc':
        return Url.staticCDNLink('eclipse/icons/favicon-dot-staging_32x32.png');
      default:
        return Url.staticCDNLink('eclipse/icons/favicon-dot_32x32.png');
    }
  }

  switch (env) {
    case 'dev':
      return Url.staticCDNLink('eclipse/icons/da_favicon_vm_v2.ico?1');
    case 'rc':
      return Url.staticCDNLink('eclipse/icons/da_favicon_staging_v2.ico');
    default:
      return Url.staticCDNLink('eclipse/icons/da_favicon_v2.ico');
  }
}

export default Head;
